import { useState, useCallback } from 'react';
import axios from 'axios';
import type { CalculateResponse, ModelComparisonItem, ScalingScenario } from '../types';
import { useCalculatorStore } from '../store/calculatorStore';

const API_BASE = '/api';

/**
 * Turn an axios failure into something actionable.
 *
 * A 422 from /calculate means the params did not validate — usually a param
 * renamed or removed while something still writes it. FastAPI names the
 * offending field in `detail`, so surface that rather than a bare
 * "Request failed with status code 422". Without this the UI silently keeps a
 * stale/empty breakdown and every section renders $0.
 */
function describeCalculationError(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const detail = e.response?.data?.detail;
    if (Array.isArray(detail)) {
      const fields = detail
        .map((d: { loc?: unknown[]; msg?: string }) => {
          const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
          return loc ? `${loc}: ${d.msg ?? 'invalid'}` : d.msg;
        })
        .filter(Boolean)
        .join('; ');
      if (fields) return `Invalid parameters — ${fields}`;
    }
    if (typeof detail === 'string') return detail;
  }
  return e instanceof Error ? e.message : 'Calculation failed';
}

export function useCalculator() {
  const projectId = useCalculatorStore((s) => s.projectId);
  const params = useCalculatorStore((s) => s.params);
  const setBreakdown = useCalculatorStore((s) => s.setBreakdown);
  const setHints = useCalculatorStore((s) => s.setHints);
  const setIsCalculating = useCalculatorStore((s) => s.setIsCalculating);
  const [error, setError] = useState<string | null>(null);

  // Must stay memoized: CalculatorView debounces on [params, calculateAll]
  // identity, so any unstable callback would thrash the setTimeout.
  const calculate = useCallback(
    async (customParams?: Record<string, unknown>) => {
      if (!projectId) return null;
      setIsCalculating(true);
      setError(null);
      try {
        const r = await axios.post<CalculateResponse>(
          `${API_BASE}/projects/${projectId}/calculate`,
          { params: customParams ?? params }
        );
        setBreakdown(r.data.breakdown);
        setHints(r.data.hints || {});
        return r.data;
      } catch (e) {
        setError(describeCalculationError(e));
        return null;
      } finally {
        setIsCalculating(false);
      }
    },
    [projectId, params, setBreakdown, setHints, setIsCalculating]
  );

  // Alias kept so existing callers keep working.
  const calculateAll = calculate;

  return { calculate, calculateAll, error };
}

export function useApplyQuestionnaire() {
  const projectId = useCalculatorStore((s) => s.projectId);
  return useCallback(
    async (answers: Record<string, unknown>) => {
      if (!projectId) throw new Error('No project selected');
      const r = await axios.post<{ params: Record<string, unknown> }>(
        `${API_BASE}/projects/${projectId}/apply-questionnaire`,
        { answers }
      );
      return r.data.params;
    },
    [projectId]
  );
}

export function useModelComparison() {
  const projectId = useCalculatorStore((s) => s.projectId);
  const params = useCalculatorStore((s) => s.params);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textModels, setTextModels] = useState<ModelComparisonItem[]>([]);
  const [voiceModels, setVoiceModels] = useState<ModelComparisonItem[]>([]);

  const compare = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post<{ text: ModelComparisonItem[]; voice: ModelComparisonItem[] }>(
        `${API_BASE}/projects/${projectId}/compare/models`,
        { params }
      );
      setTextModels(r.data.text);
      setVoiceModels(r.data.voice);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Comparison failed');
    } finally {
      setLoading(false);
    }
  }, [projectId, params]);

  return { compare, loading, error, textModels, voiceModels };
}

export function useScalingComparison() {
  const projectId = useCalculatorStore((s) => s.projectId);
  const params = useCalculatorStore((s) => s.params);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [axis, setAxis] = useState<string>('users');
  const [scenarios, setScenarios] = useState<ScalingScenario[]>([]);
  const [servicesToPlot, setServicesToPlot] = useState<{ id: string; name: string }[]>([]);

  const compare = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post<{
        axis: string;
        services_to_plot: { id: string; name: string }[];
        scenarios: ScalingScenario[];
      }>(`${API_BASE}/projects/${projectId}/compare/scaling`, { params });
      setAxis(r.data.axis);
      setScenarios(r.data.scenarios);
      setServicesToPlot(r.data.services_to_plot);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scaling comparison failed');
    } finally {
      setLoading(false);
    }
  }, [projectId, params]);

  return { compare, loading, error, axis, scenarios, servicesToPlot };
}
